'use client';
import { normalizeCustomerMobileInput } from '@/lib/partner-customer/presentation';
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Copy, Loader2, MessageCircle, Plus, CheckCircle2 } from 'lucide-react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { DIRECT_CONSENT, DIRECT_WHATSAPP_CONSENT, applicationWhatsappMessage, validSavedApplicationLink } from '@/lib/partner-customer/forwarding';

// Keep partial input editable; never silently truncate a pasted destination.
export function normalizeIntakeMobileInput(value: string) {
  return normalizeCustomerMobileInput(value);
}

export type IntakeProduct = { productCode: string; productName: string; category: string; lenderName: string; defaultMode?:'direct'|'portal'; availableModes?:('direct'|'portal')[] };
const legacyProduct: IntakeProduct = {productCode:'PFL_STPL',productName:'Personal Loan',category:'Personal Loan',lenderName:'Poonawalla Fincorp'};

export default function DirectCustomerIntake({
  partnerId,
  partnerCode,
  publicEntry = false,
  demo = false,
  managedWhatsappEnabled = false,
  products = [legacyProduct],
  initialProductCode,
  onDemoSaved,
  onViewCases,
  onSaved,
}: {
  partnerId?: string;
  partnerCode?: string;
  publicEntry?: boolean;
  demo?: boolean;
  managedWhatsappEnabled?: boolean;
  products?: IntakeProduct[];
  initialProductCode?: string;
  onDemoSaved?: (data: {
    name: string;
    mobile: string;
    applicationNo: string;
    customerLink: string;
  }) => void;
  onViewCases?: () => void;
  onSaved?: () => void;
}) {
  const requestId = useRef<string | null>(null);
  const pending = useRef(false);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [consent, setConsent] = useState(false);
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retrySameDetails, setRetrySameDetails] = useState(false);
  const [productCode, setProductCode] = useState(initialProductCode || products[0]?.productCode || '');
  const selected = products.find(product => product.productCode === productCode);
  const [chosenMode,setChosenMode]=useState<'direct'|'portal'|null>(null);
  const availableModes=selected?.availableModes||['direct'];
  const preferredMode=chosenMode||selected?.defaultMode||'direct';
  const mode=availableModes.includes(preferredMode)?preferredMode:availableModes[0];
  const [saved, setSaved] = useState<{
    leadId?: string;
    applicationNo: string;
    trackingUrl: string;
    lenderName?: string;
    productName?: string;
    mode?:'direct'|'portal';
  } | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendAttempted, setSendAttempted] = useState(false);
  useEffect(() => {
    if (!publicEntry || demo) return;
    try {
      const value = window.sessionStorage.getItem('rupiqa-website-mobile') || '';
      if (/^[6-9]\d{9}$/.test(value)) setMobile(value);
      window.sessionStorage.removeItem('rupiqa-website-mobile');
    } catch {
      /* Manual entry remains available when browser storage is disabled. */
    }
  }, [publicEntry, demo]);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (pending.current || !selected) return;
    pending.current = true;
    setBusy(true);
    setError('');
    requestId.current ||= crypto.randomUUID();
    try {
      if (demo) {
        const applicationNo = `RQ${requestId.current.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
        const trackingUrl = `${window.location.origin}/rupiqa/partner/demo/customer?demoRef=${applicationNo}`;
        setSaved({ applicationNo, trackingUrl });
        onDemoSaved?.({ name: name.trim(), mobile, applicationNo, customerLink: trackingUrl });
        return;
      }
      const endpoint = publicEntry
        ? '/api/rupiqa/direct-intake'
        : `/api/rupiqa/partner/direct-intake?partner_id=${encodeURIComponent(partnerId || '')}`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: requestId.current,
          name: name.trim(),
          mobile,
          loanType: selected.productCode === 'PFL_STPL' ? 'Personal Loan' : selected.category,
          productCode: selected.productCode,
          mode,
          consent,
          whatsappConsent,
          ...(publicEntry && partnerCode ? { partnerCode } : {}),
        }),
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true) {
        if ([400,401,403,409,429].includes(response.status)) {requestId.current=null; setRetrySameDetails(false);}
        else setRetrySameDetails(true);
        throw new Error(result.error || 'Save could not be confirmed. Please retry.');
      }
      if (!validSavedApplicationLink(result.trackingUrl, result.applicationNo, result.mode || mode))
        throw new Error('Application link unavailable. Contact support before retrying.');
      setSaved({...result, lenderName: result.lenderName || selected.lenderName, productName: result.productName || selected.productName});
      onSaved?.();
    } catch (failure) {
      if (requestId.current) setRetrySameDetails(true);
      setError(failure instanceof Error ? failure.message : 'Unable to save. Please retry.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  const input =
    'mt-2 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100';
  async function sendManagedMessage() {
    if (demo || !managedWhatsappEnabled || !saved?.leadId || sendAttempted || pending.current) return;
    pending.current = true;
    setSendAttempted(true);
    setBusy(true);
    setError('');
    setNotice('');
    setSendOpen(false);
    try {
      const response = await fetch(
        `/api/rupiqa/partner/referral-whatsapp?partner_id=${encodeURIComponent(partnerId || '')}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ leadId: saved.leadId, confirm: true }),
        }
      );
      const result = await response.json();
      if (!response.ok || result.waSent !== true)
        throw new Error(
          result.error ||
            result.waError ||
            'Message acceptance not confirmed. Check history before retrying.'
        );
      setNotice('WhatsApp provider accepted the message. Delivery is not yet confirmed.');
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Message outcome unknown. Check history before retrying.'
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (saved)
    return (
      <section className="mx-auto max-w-2xl space-y-5">
        <header className="flex items-start gap-3">
          <CheckCircle2 className="mt-1 shrink-0 text-emerald-600" size={24} />
          <div>
            <h1 className="text-xl font-bold">
              {publicEntry ? 'Your application is ready' : 'Customer saved'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">Application {saved.applicationNo}</p>
          </div>
        </header>
        <div className="border-y border-slate-200 py-4">
          <p className="font-semibold">{name}</p>
          <p className="mt-1 text-sm text-slate-600">{saved.productName || selected?.productName} · {saved.lenderName || selected?.lenderName}</p>
        </div>
        {demo && (
          <p className="text-xs text-amber-700">
            Sample application only. This link opens the isolated demo journey.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="text-sm text-emerald-700">
            {notice}
          </p>
        )}
        {publicEntry ? (
          <>
            <a
              href={saved.trackingUrl}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 font-semibold text-white"
            >
              {saved.mode==='portal'?'Continue application':'Continue to lender'}
              <ArrowRight size={18} />
            </a>
            <p className="text-xs text-slate-500">
              {saved.mode==='portal'?'Verify your mobile to securely complete your details and documents.':`You will complete the application with ${saved.lenderName || selected?.lenderName}. Approval and terms are decided by the lender.`}
            </p>
          </>
        ) : (
          <>
            <label className="block text-sm font-medium">
              Customer application link
              <input
                readOnly
                aria-label="Customer tracking link"
                value={saved.trackingUrl}
                className={input}
              />
            </label>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(saved.trackingUrl);
                    setNotice('Link copied');
                    setError('');
                  } catch {
                    setError('Copy failed. Select the link and copy it manually.');
                  }
                }}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-4 text-sm font-semibold"
              >
                <Copy size={16} />
                Copy link
              </button>
              {whatsappConsent && (
                <a
                  href={`https://wa.me/91${mobile}?text=${encodeURIComponent(applicationWhatsappMessage(name, saved.trackingUrl, saved.mode==='portal'?'RUPIQA':saved.lenderName || selected?.lenderName))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-sm font-semibold text-white"
                >
                  <MessageCircle size={17} />
                  Share on WhatsApp
                </a>
              )}
            </div>
            {whatsappConsent && <p className="text-xs text-slate-500">
              WhatsApp opens a prepared message. Tap Send to share.
            </p>}
            {managedWhatsappEnabled && whatsappConsent && saved.leadId && (
              <AlertDialog.Root open={sendOpen} onOpenChange={setSendOpen}>
                <AlertDialog.Trigger asChild>
                  <button
                    disabled={sendAttempted || busy}
                    className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-blue-200 px-4 text-sm font-semibold text-blue-700 disabled:opacity-50"
                  >
                    <MessageCircle size={16} />
                    Send via RUPIQA WhatsApp
                  </button>
                </AlertDialog.Trigger>
                <AlertDialog.Portal>
                  <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
                  <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%_-_2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-5 shadow-xl">
                    <AlertDialog.Title className="text-lg font-semibold">
                      Send application link?
                    </AlertDialog.Title>
                    <AlertDialog.Description className="mt-3 text-sm leading-relaxed text-slate-600">
                      Send the approved WhatsApp message to +91 {mobile} for application{' '}
                      {saved.applicationNo}. Customer consent is recorded. Provider charges may
                      apply.
                    </AlertDialog.Description>
                    <div className="mt-5 flex justify-end gap-3">
                      <AlertDialog.Cancel className="min-h-11 rounded-lg border px-4 text-sm">
                        Cancel
                      </AlertDialog.Cancel>
                      <button
                        onClick={sendManagedMessage}
                        className="min-h-11 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white"
                      >
                        Send message
                      </button>
                    </div>
                  </AlertDialog.Content>
                </AlertDialog.Portal>
              </AlertDialog.Root>
            )}
            <div className="flex flex-wrap gap-4 border-t border-slate-200 pt-4">
              {onViewCases && (
                <button
                  type="button"
                  onClick={onViewCases}
                  className="min-h-11 text-sm font-semibold text-blue-700"
                >
                  View My Cases
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setSaved(null);
                  setSendAttempted(false);
                  setName('');
                  setMobile('');
                  setConsent(false);
                  setWhatsappConsent(false);
                  setNotice('');
                  setError('');
                  requestId.current = null;
                  setRetrySameDetails(false);
                }}
                className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-blue-700"
              >
                <Plus size={16} />
                Add another customer
              </button>
            </div>
          </>
        )}
      </section>
    );
  return (
    <form onSubmit={submit} className="mx-auto max-w-2xl space-y-5">
      <header>
        <h1 className={`text-xl font-bold ${publicEntry ? '' : 'hidden md:block'}`}>
          {publicEntry ? 'Start your application' : 'Add Customer'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{selected ? `${selected.productName} · ${selected.lenderName}` : 'No available products'}</p>
      </header>
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p>{error}</p>
          {!publicEntry && onViewCases && (
            <button type="button" onClick={onViewCases} className="mt-1 min-h-11 font-semibold text-blue-700 underline">
              Check My Cases
            </button>
          )}
        </div>
      )}
      <fieldset disabled={busy} className="space-y-5 disabled:opacity-60">
        <label className="block text-sm font-medium">
          Full name *
          <input
            required
            readOnly={retrySameDetails}
            minLength={2}
            maxLength={100}
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={input}
          />
        </label>
        <label className="block text-sm font-medium">
          Mobile number *
          <div className="mt-2 flex min-h-11 overflow-hidden rounded-lg border border-slate-300 focus-within:ring-2 focus-within:ring-blue-100">
            <span className="grid place-items-center border-r border-slate-200 bg-slate-50 px-3 text-sm text-slate-600">
              +91
            </span>
            <input
              required
              readOnly={retrySameDetails}
              aria-label="Mobile number"
              inputMode="tel"
              autoComplete="tel-national"
              pattern="[6-9][0-9]{9}"
              title="Enter a valid 10-digit Indian mobile number"
              value={mobile}
              onChange={(event) => setMobile(normalizeIntakeMobileInput(event.target.value))}
              className="min-w-0 flex-1 px-3 text-base outline-none"
            />
          </div>
        </label>
        <label className="block text-sm font-medium">
          Loan product and lender
          <select disabled={retrySameDetails} className={input} value={productCode} onChange={event => {setProductCode(event.target.value); setChosenMode(null); setConsent(false); requestId.current=null;}}>
            {products.map(product => <option key={product.productCode} value={product.productCode}>{product.productName} · {product.lenderName}</option>)}
          </select>
        </label>
        {!publicEntry&&availableModes.length>1&&<label className="block text-sm font-medium">Customer journey<select className={input} value={mode} disabled={retrySameDetails} onChange={event=>{setChosenMode(event.target.value as 'direct'|'portal');setConsent(false);requestId.current=null;}}>{availableModes.map(value=><option key={value} value={value}>{value==='portal'?'Complete with RUPIQA':'Continue directly to lender'}</option>)}</select></label>}
        <div className="rounded-lg bg-slate-50 px-3 py-3 text-sm"><span className="font-medium">Next step: </span>{mode==='portal'?'Securely complete details and documents with RUPIQA':`Continue on ${selected?.lenderName || 'the lender'} website`}</div>
        <div className="space-y-3 border-t border-slate-200 pt-4">
          <label className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
            <input
              required
              disabled={retrySameDetails}
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0"
            />
            <span>{!publicEntry && 'Customer has agreed: '}{mode==='portal'?'I consent to RUPIQA recording and processing my details for this application. Loan approval is not guaranteed.':selected?.productCode === 'PFL_STPL' ? DIRECT_CONSENT : `I consent to RUPIQA recording my name, mobile and loan requirement and sharing this application reference with ${selected?.lenderName || 'the selected lender'}. Lender approval is not guaranteed.`}</span>
          </label>
          <label className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
            <input
              type="checkbox"
              checked={whatsappConsent}
              disabled={retrySameDetails}
              onChange={(event) => setWhatsappConsent(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0"
            />
            <span>
              {publicEntry
                ? DIRECT_WHATSAPP_CONSENT
                : 'Customer has agreed to receive this application link on WhatsApp.'}
            </span>
          </label>
        </div>
        <button
          type="submit"
          disabled={busy || !selected}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 font-semibold text-white disabled:opacity-50"
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
          {busy ? 'Saving...' : publicEntry ? 'Save & Continue' : 'Save & Create Link'}
        </button>
      </fieldset>
    </form>
  );
}
